import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SearchBar, Modal, TableFlat } from "@veorest/ui";
import { UserCircle } from "lucide-react";
import { fetchRestaurantCustomer, fetchRestaurantCustomers } from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatIqd, formatNumber, statusPillClass } from "../lib/format";

export default function CustomersRestaurantPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin-customers-restaurant", search, page],
    queryFn: () => fetchRestaurantCustomers({ search, page, limit: 25 }),
  });

  const detail = useQuery({
    queryKey: ["admin-customer-restaurant", selectedId],
    queryFn: () => fetchRestaurantCustomer(selectedId!),
    enabled: Boolean(selectedId),
  });

  const totalPages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 25));
  const items = (list.data?.items ?? []) as Array<Record<string, unknown> & { id: string }>;

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Customers"
        title="Client customers"
        subtitle="Per-client CRM customers — never mixed with global app accounts."
      />

      <AdminPanel
        toolbar={
          <SearchBar
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search name, phone, email…"
            className="admin-search"
          />
        }
      >
        {list.isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 rounded-xl skeleton" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<UserCircle className="h-5 w-5" />}
            title="No client customers"
            body="CRM records created via orders will show up here."
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={[
                {
                  key: "name",
                  header: "Customer",
                  render: (row) => (
                    <div className="admin-row-main">
                      <span className="admin-row-avatar" aria-hidden>
                        {String(row.name || "?").slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="admin-row-title">{String(row.name)}</p>
                        <p className="admin-row-sub">{String(row.phone || "—")}</p>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "restaurant_name",
                  header: "Client",
                  render: (row) => String(row.restaurant_name),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (row) => (
                    <span className={statusPillClass(String(row.status || "regular"))}>
                      {String(row.status || "regular")}
                    </span>
                  ),
                },
                {
                  key: "total_orders",
                  header: "Orders",
                  render: (row) => (
                    <span className="tabular font-bold">{formatNumber(Number(row.total_orders))}</span>
                  ),
                },
                {
                  key: "total_spent",
                  header: "Spent",
                  render: (row) => formatNumber(Number(row.total_spent)),
                },
              ]}
              data={items}
              keyField="id"
              onRowClick={(row) => setSelectedId(String(row.id))}
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={list.data?.total ?? 0}
        label="customers"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <Modal open={Boolean(selectedId)} onClose={() => setSelectedId(null)} panelClassName="max-w-2xl">
        <div className="p-6">
          <p className="menu-page__eyebrow mb-1">Client CRM</p>
          <h2 className="text-xl font-black tracking-tight mb-4">Customer detail</h2>
          {detail.isLoading ? (
            <div className="h-24 skeleton rounded-xl" />
          ) : (
            <>
              <div className="admin-detail-grid mb-5">
                <div>
                  <p className="admin-detail-label">Name</p>
                  <p className="admin-detail-value">{String(detail.data?.customer.name)}</p>
                </div>
                <div>
                  <p className="admin-detail-label">Client</p>
                  <p className="admin-detail-value">
                    {String(detail.data?.customer.restaurant_name)}
                  </p>
                </div>
                <div>
                  <p className="admin-detail-label">Phone</p>
                  <p className="admin-detail-value">{String(detail.data?.customer.phone || "—")}</p>
                </div>
                <div>
                  <p className="admin-detail-label">Spent</p>
                  <p className="admin-detail-value">
                    {formatIqd(Number(detail.data?.customer.total_spent))}
                  </p>
                </div>
              </div>
              <h3 className="font-bold mb-2">Order history</h3>
              <div className="admin-table-wrap max-h-64 overflow-auto">
                <table className="table-flat">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Channel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.data?.orders as Array<Record<string, unknown>> ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-6 text-[var(--text-muted)]">
                          No orders
                        </td>
                      </tr>
                    ) : (
                      (detail.data?.orders as Array<Record<string, unknown>>).map((o) => (
                        <tr key={String(o.id)}>
                          <td>{formatDate(String(o.created_at))}</td>
                          <td>{String(o.status)}</td>
                          <td>{formatNumber(Number(o.total))}</td>
                          <td>{String(o.channel || "—")}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
